import * as React from 'react';

/**
 * Controls shared from the host to the `Modal` shell: open state + close.
 * The payload is NOT carried here — it is passed to the modal component as a
 * typed prop (`ModalRenderProps`), so there is no unchecked payload cast.
 */
export interface ModalControls {
  /** True while the Dialog is open (drives the exit animation). */
  open: boolean;
  /** Close this modal instance. */
  close: () => void;
}

const ModalControlsContext = React.createContext<ModalControls | null>(null);

export function ModalControlsProvider({
  value,
  children,
}: {
  value: ModalControls;
  children: React.ReactNode;
}) {
  return (
    <ModalControlsContext.Provider value={value}>
      {children}
    </ModalControlsContext.Provider>
  );
}

/** Read the enclosing modal's open/close controls (used by the `Modal` shell). */
export function useModalControls(): ModalControls {
  const ctx = React.useContext(ModalControlsContext);
  if (!ctx) {
    throw new Error(
      'useModalControls must be used within a modal rendered by <ModalHost>.',
    );
  }
  return ctx;
}
